import { jsx as _jsx } from "react/jsx-runtime";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useRef, useState } from "react";
/**
 * One clip plays at a time on a page: pressing play on a second button
 * stops the first, because two voices speaking over each other says
 * nothing about either.
 */
let current = null;
function takeOver(el, stop) {
    current?.stop();
    current = { el, stop };
}
/**
 * Play / stop for a sample clip whose URL may not exist yet: `src` is asked
 * on press and may take a moment (a clip synthesised on first ask), so the
 * button shows it is working rather than appearing dead. A null URL means
 * there is nothing to play; the button says why in its tooltip.
 */
export function SamplePlayButton({ src, title = "Hear this voice", unavailable, disabled = false, size = 40, onError, }) {
    const audio = useRef(null);
    const [state, setState] = useState("idle");
    // A press that resolves after an unmount, or after a stop, must not
    // start a clip nobody can stop.
    const generation = useRef(0);
    const stop = () => {
        generation.current += 1;
        audio.current?.pause();
        audio.current = null;
        setState("idle");
    };
    // Unmount stops whatever is playing; the ref is read then, not now.
    useEffect(() => {
        return () => {
            generation.current += 1;
            audio.current?.pause();
            audio.current = null;
        };
    }, []);
    const press = async () => {
        if (state !== "idle") {
            stop();
            return;
        }
        generation.current += 1;
        const mine = generation.current;
        setState("loading");
        let url = null;
        try {
            url = await src();
        }
        catch (error) {
            if (mine === generation.current)
                setState("idle");
            onError?.(error instanceof Error ? error.message : "The sample could not be fetched");
            return;
        }
        if (mine !== generation.current)
            return;
        if (!url) {
            setState("idle");
            onError?.(unavailable ?? "No sample for this voice");
            return;
        }
        const el = new Audio(url);
        el.onended = () => {
            if (audio.current === el)
                stop();
        };
        el.onerror = () => {
            if (audio.current === el) {
                stop();
                onError?.("The sample could not be played");
            }
        };
        audio.current = el;
        takeOver(el, stop);
        setState("playing");
        try {
            await el.play();
        }
        catch {
            if (audio.current === el)
                stop();
        }
    };
    const label = state === "playing" ? "Stop" : state === "loading" ? "Fetching the sample" : title;
    return (_jsx(Tooltip, { title: disabled && unavailable ? unavailable : label, children: _jsx("span", { children: _jsx(IconButton, { "aria-label": label, disabled: disabled, onClick: () => void press(), sx: {
                    width: size,
                    height: size,
                    border: "1px solid",
                    borderColor: "divider",
                }, children: state === "playing" ? (_jsx(StopIcon, { fontSize: "small" })) : state === "loading" ? (_jsx(CircularProgress, { size: size * 0.45 })) : (_jsx(PlayArrowIcon, { fontSize: "small" })) }) }) }));
}
//# sourceMappingURL=SamplePlayButton.js.map